import React, { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useParams } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {
  ArrowLeft,
  MapPin,
  Banknote,
  Clock,
  Calendar,
  CheckCircle2,
  Send,
  Loader2,
  AlertCircle,
  Share2,
  Check,
  Briefcase,
} from "lucide-react"
import {
  MappedPublicJob,
  HrmFormField,
  fetchPublicJobDetail,
  submitJobApplication,
  getAppliedJobIds,
} from "./recruitment.api"
import { ROUTES } from "@/constants"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export default function RecruitmentDetailPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const location = useLocation()
  const applySectionRef = useRef<HTMLDivElement>(null)

  const [job, setJob] = useState<MappedPublicJob | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [copiedLink, setCopiedLink] = useState<boolean>(false)

  // Dynamic HRM form state
  const [formValues, setFormValues] = useState<Record<string, any>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [alreadyApplied, setAlreadyApplied] = useState<boolean>(false)

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior })
  }, [jobId])

  useEffect(() => {
    if (!jobId) return
    let cancelled = false
    setIsLoading(true)
    setIsSubmitted(false)
    setSubmitError("")
    setFieldErrors({})
    setAlreadyApplied(getAppliedJobIds().includes(jobId))

    fetchPublicJobDetail(jobId)
      .then(fullJob => {
        if (cancelled) return
        setJob(fullJob)
        const fields = fullJob.form_template?.fields || []
        const defaults: Record<string, any> = {}
        fields.forEach(field => {
          if (!field.name) return
          if (field.field_type === "checkbox") {
            defaults[field.name] = []
          } else if (field.name === "recruitment_source") {
            const webOption = field.options?.find(o => o.value === "0")
            defaults[field.name] = webOption ? "0" : ""
          } else {
            defaults[field.name] = ""
          }
        })
        setFormValues(defaults)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [jobId])

  // Auto-scroll to application form if URL has #apply
  useEffect(() => {
    if (!isLoading && job && location.hash === "#apply") {
      const timer = setTimeout(() => {
        applySectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isLoading, job, location.hash])

  const sortedFields: HrmFormField[] = useMemo(() => {
    const rawFields = job?.form_template?.fields || []
    return [...rawFields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  }, [job])

  const scrollToApplyForm = () => {
    applySectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href.split("#")[0])
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    } catch {
      // ignore clipboard errors
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    for (const field of sortedFields) {
      const val = formValues[field.name]

      if (field.name === "phone") {
        const str = String(val ?? "").trim()
        if (field.is_required && !str) {
          errors[field.name] = "Trường này là bắt buộc"
        } else if (str && !/^0\d{9}$/.test(str)) {
          errors[field.name] =
            "Số điện thoại không hợp lệ (10 chữ số, bắt đầu bằng số 0)"
        }
        continue
      }

      if (field.name === "email") {
        const str = String(val ?? "").trim()
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const domainRegex = /@(gmail\.com|.*\.edu(\.vn)?)$/i
        if (field.is_required && !str) {
          errors[field.name] = "Trường này là bắt buộc"
        } else if (str && (!emailRegex.test(str) || !domainRegex.test(str))) {
          errors[field.name] = "Email không hợp lệ (dùng @gmail.com hoặc .edu)"
        }
        continue
      }

      if (field.name === "preferred_store") {
        const arr = Array.isArray(val) ? val : []
        if (field.is_required && arr.length === 0) {
          errors[field.name] = "Vui lòng chọn ít nhất 1 khu vực đăng ký"
        } else if (arr.length > 3) {
          errors[field.name] = "Chỉ được chọn tối đa 3 khu vực"
        }
        continue
      }

      if (field.field_type === "checkbox") {
        const arr = Array.isArray(val) ? val : []
        if (field.is_required && arr.length === 0) {
          errors[field.name] = "Vui lòng chọn ít nhất 1 mục"
        }
        continue
      }

      if (field.is_required) {
        const str = String(val ?? "").trim()
        if (!str) {
          errors[field.name] = "Trường này là bắt buộc"
        }
      }
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFieldChange = (name: string, value: any) => {
    setFormValues(prev => ({ ...prev, [name]: value }))
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const toggleCheckboxOption = (
    fieldName: string,
    optionValue: string,
    maxLimit?: number
  ) => {
    const current: string[] = Array.isArray(formValues[fieldName])
      ? formValues[fieldName]
      : []
    if (current.includes(optionValue)) {
      handleFieldChange(
        fieldName,
        current.filter(item => item !== optionValue)
      )
    } else {
      if (maxLimit && current.length >= maxLimit) {
        setFieldErrors(prev => ({
          ...prev,
          [fieldName]: `Chỉ được chọn tối đa ${maxLimit} mục`,
        }))
        return
      }
      handleFieldChange(fieldName, [...current, optionValue])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!job) return
    setSubmitError("")

    if (!validateForm()) {
      setSubmitError("Vui lòng kiểm tra lại các trường thông tin bên dưới.")
      return
    }

    setIsSubmitting(true)
    try {
      await submitJobApplication({
        jobUuid: job.uuid,
        companyName: job.company_name || "TUTIMI",
        fields: sortedFields,
        values: formValues,
      })
      setIsSubmitted(true)
      setAlreadyApplied(true)
      applySectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    } catch (err: any) {
      setSubmitError(
        err?.message || "Có lỗi xảy ra khi gửi hồ sơ. Vui lòng thử lại."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex flex-col items-center justify-center py-24 px-4">
        <Loader2
          size={36}
          className="animate-spin text-[#1f4171] mb-3"
          aria-hidden="true"
        />
        <p className="text-sm font-semibold text-[#1f4171]">
          Đang tải chi tiết công việc...
        </p>
      </div>
    )
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] py-20 px-4">
        <div className="mx-auto max-w-xl text-center space-y-4">
          <h1 className="text-2xl font-bold text-[#1f4171]">
            Không tìm thấy vị trí tuyển dụng
          </h1>
          <p className="text-sm text-slate-600">
            Vị trí này có thể đã hết hạn hoặc đường dẫn không tồn tại.
          </p>
          <Button asChild>
            <Link to={ROUTES.RECRUITMENT}>
              <ArrowLeft size={16} aria-hidden="true" />
              Quay lại danh sách tuyển dụng
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#f5f7fa] text-[#142d4f]">
      <Helmet>
        <title>{`${job.title} – Tuyển Dụng TuTiMi`}</title>
        <meta
          name="description"
          content={`Ứng tuyển vị trí ${job.title} (${job.salary_display}) tại ${job.location} - TuTiMi Coffee & Tea.`}
        />
      </Helmet>

      {/* FLAT CAREER DETAIL HEADER (Ashby / Lever / Greenhouse inspired) */}
      <section className="bg-[#1f4171] text-white py-10 sm:py-14">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 space-y-6">
          <div>
            <Link
              to={ROUTES.RECRUITMENT}
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-white/80 hover:text-[#2ddac0] transition-colors"
            >
              <ArrowLeft size={16} aria-hidden="true" />
              Tất cả vị trí tuyển dụng
            </Link>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="space-y-3.5 max-w-3xl">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-[#2ddac0] text-[#142d4f]">
                  {job.block_label}
                </Badge>
                {job.tags.map(tag => (
                  <Badge
                    key={tag}
                    className="bg-white/15 text-white font-medium"
                  >
                    <Clock size={12} aria-hidden="true" />
                    {tag}
                  </Badge>
                ))}
                {alreadyApplied && (
                  <Badge className="bg-emerald-500 text-white">
                    <CheckCircle2 size={12} aria-hidden="true" />
                    Đã ứng tuyển
                  </Badge>
                )}
              </div>

              <h1 className="text-2xl sm:text-4xl lg:text-[40px] font-bold tracking-tight text-white leading-tight">
                {job.title}
              </h1>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-white/85 pt-1">
                <span className="inline-flex items-center gap-1.5 font-bold text-[#2ddac0] text-sm sm:text-base">
                  <Banknote size={18} aria-hidden="true" />
                  {job.salary_display}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={16} aria-hidden="true" />
                  {job.location}
                </span>
                {job.recruitment_period !== "-" && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar size={16} aria-hidden="true" />
                    Hạn nộp hồ sơ: {job.recruitment_period}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <Button
                type="button"
                variant="accent"
                size="lg"
                onClick={scrollToApplyForm}
              >
                <Send size={16} aria-hidden="true" />
                {alreadyApplied ? "Ứng tuyển lại" : "Ứng tuyển vị trí này"}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN 2-COLUMN INTERNATIONAL CAREER LAYOUT */}
      <section className="py-8 sm:py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* LEFT COLUMN: Full Rich-Text Job Description + Direct Application Form */}
            <div className="lg:col-span-8 space-y-8">
              {/* 1. Full Rich-Text Job Description Card */}
              <Card>
                <CardContent className="p-6 sm:p-10">
                  {job.description_html ? (
                    <div
                      className="text-sm sm:text-base leading-relaxed text-slate-700 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-[#1f4171] [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-[#1f4171] [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-[#1f4171] [&_strong]:text-[#1f4171] [&_p]:mb-3 [&_p:empty]:hidden [&_ul]:my-3 [&_ul]:ml-6 [&_ul]:list-disc [&_ol]:my-3 [&_ol]:ml-6 [&_ol]:list-decimal [&_li]:mb-1.5 [&_li_p]:mb-0"
                      dangerouslySetInnerHTML={{
                        __html: job.description_html,
                      }}
                    />
                  ) : (
                    <p className="text-sm text-slate-600">
                      Chưa có mô tả chi tiết cho vị trí này.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* 2. Direct Application Form Section */}
              <div
                id="apply"
                ref={applySectionRef}
                className="scroll-mt-24"
              >
                <Card>
                  <CardContent className="p-6 sm:p-10">
                    {isSubmitted ? (
                      <div className="py-10 text-center max-w-md mx-auto space-y-5">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#e8fcf9] text-[#0f766e]">
                          <CheckCircle2 size={36} aria-hidden="true" />
                        </div>
                        <div className="space-y-2">
                          <h2 className="text-2xl font-bold text-[#1f4171]">
                            Đã gửi hồ sơ ứng tuyển!
                          </h2>
                          <p className="text-sm text-slate-600 leading-relaxed">
                            Hồ sơ ứng tuyển vị trí{" "}
                            <strong className="text-[#1f4171]">
                              {job.title}
                            </strong>{" "}
                            của{" "}
                            <strong className="text-[#1f4171]">
                              {formValues.fullname || "bạn"}
                            </strong>{" "}
                            đã được ghi nhận vào hệ thống HRM TuTiMi. Bộ phận
                            Nhân sự sẽ liên hệ qua SĐT/Zalo{" "}
                            <strong>{formValues.phone}</strong>.
                          </p>
                        </div>

                        <div className="flex flex-wrap justify-center gap-3 pt-2">
                          <Button asChild variant="secondary">
                            <Link to={ROUTES.RECRUITMENT}>
                              Xem vị trí tuyển dụng khác
                            </Link>
                          </Button>
                          <Button
                            type="button"
                            onClick={() => setIsSubmitted(false)}
                          >
                            Gửi hồ sơ khác
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <form
                        onSubmit={handleSubmit}
                        className="space-y-6"
                        noValidate
                      >
                        <div className="space-y-1.5">
                          <Badge variant="secondary">Ứng tuyển trực tuyến</Badge>
                          <h2 className="text-xl sm:text-2xl font-bold text-[#1f4171]">
                            Nộp Hồ Sơ Ứng Tuyển – {job.title}
                          </h2>
                          <p className="text-xs sm:text-sm text-slate-600">
                            {job.form_template?.description ||
                              "Điền thông tin của bạn để bộ phận Nhân sự TuTiMi liên hệ phỏng vấn."}{" "}
                            Các trường có dấu{" "}
                            <span className="text-red-500 font-bold">*</span> là
                            bắt buộc.
                          </p>
                        </div>

                        {submitError && (
                          <div
                            role="alert"
                            className="flex items-start gap-2.5 rounded-xl bg-red-50 px-4 py-3 text-xs sm:text-sm font-medium text-red-700"
                          >
                            <AlertCircle
                              size={18}
                              className="shrink-0 mt-0.5 text-red-600"
                              aria-hidden="true"
                            />
                            <span>{submitError}</span>
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          {sortedFields.map(field => {
                            const isFullWidth =
                              field.field_type === "checkbox" ||
                              field.field_type === "textarea" ||
                              field.name === "current_location" ||
                              field.name === "interview_location"
                            const errorMsg = fieldErrors[field.name]
                            const fieldId = `hrm-field-${field.name}`

                            return (
                              <div
                                key={field.uuid || field.name}
                                className={
                                  isFullWidth
                                    ? "sm:col-span-2 space-y-2"
                                    : "space-y-2"
                                }
                              >
                                <Label
                                  htmlFor={
                                    field.field_type === "checkbox" ||
                                    field.field_type === "radio" ||
                                    field.field_type === "select"
                                      ? undefined
                                      : fieldId
                                  }
                                >
                                  {field.label}
                                  {field.is_required && (
                                    <span className="text-red-500 ml-1">*</span>
                                  )}
                                  {field.name === "preferred_store" && (
                                    <span className="ml-2 text-xs font-normal text-slate-500">
                                      (Chọn tối đa 3 khu vực)
                                    </span>
                                  )}
                                </Label>

                                {field.field_type === "text" && (
                                  <Input
                                    id={fieldId}
                                    type={
                                      field.name === "phone"
                                        ? "tel"
                                        : field.name === "email"
                                        ? "email"
                                        : "text"
                                    }
                                    value={formValues[field.name] ?? ""}
                                    onChange={e =>
                                      handleFieldChange(
                                        field.name,
                                        e.target.value
                                      )
                                    }
                                    placeholder="Nhập thông tin..."
                                    className="bg-[#f5f7fa] focus-visible:bg-[#eef3f9]"
                                  />
                                )}

                                {field.field_type === "number" && (
                                  <Input
                                    id={fieldId}
                                    type="number"
                                    value={formValues[field.name] ?? ""}
                                    onChange={e =>
                                      handleFieldChange(
                                        field.name,
                                        e.target.value
                                      )
                                    }
                                    className="bg-[#f5f7fa] focus-visible:bg-[#eef3f9]"
                                  />
                                )}

                                {field.field_type === "date" && (
                                  <Input
                                    id={fieldId}
                                    type="date"
                                    value={formValues[field.name] ?? ""}
                                    onChange={e =>
                                      handleFieldChange(
                                        field.name,
                                        e.target.value
                                      )
                                    }
                                    className="bg-[#f5f7fa] focus-visible:bg-[#eef3f9]"
                                  />
                                )}

                                {field.field_type === "textarea" && (
                                  <Textarea
                                    id={fieldId}
                                    rows={3}
                                    value={formValues[field.name] ?? ""}
                                    onChange={e =>
                                      handleFieldChange(
                                        field.name,
                                        e.target.value
                                      )
                                    }
                                  />
                                )}

                                {field.field_type === "select" && (
                                  <Select
                                    value={formValues[field.name] ?? ""}
                                    onValueChange={val =>
                                      handleFieldChange(field.name, val)
                                    }
                                  >
                                    <SelectTrigger className="bg-[#f5f7fa]">
                                      <SelectValue placeholder="-- Chọn --" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(field.options || []).map(opt => (
                                        <SelectItem
                                          key={opt.value}
                                          value={opt.value}
                                        >
                                          {opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}

                                {field.field_type === "checkbox" && (
                                  <div className="rounded-xl bg-[#f5f7fa] p-3.5">
                                    <div
                                      className={`grid gap-2 ${
                                        (field.options?.length || 0) > 6
                                          ? "grid-cols-2 sm:grid-cols-3"
                                          : "grid-cols-1 sm:grid-cols-2"
                                      }`}
                                    >
                                      {(field.options || []).map(opt => {
                                        const selectedArr: string[] =
                                          Array.isArray(formValues[field.name])
                                            ? formValues[field.name]
                                            : []
                                        const isChecked = selectedArr.includes(
                                          opt.value
                                        )
                                        const maxLimit =
                                          field.name === "preferred_store"
                                            ? 3
                                            : undefined

                                        return (
                                          <div
                                            key={opt.value}
                                            onClick={() =>
                                              toggleCheckboxOption(
                                                field.name,
                                                opt.value,
                                                maxLimit
                                              )
                                            }
                                            className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-xs sm:text-sm transition-colors duration-150 cursor-pointer select-none ${
                                              isChecked
                                                ? "bg-[#1f4171] text-white font-semibold"
                                                : "bg-white text-slate-700 hover:bg-[#e2e8f0]"
                                            }`}
                                          >
                                            <Checkbox
                                              checked={isChecked}
                                              onCheckedChange={() =>
                                                toggleCheckboxOption(
                                                  field.name,
                                                  opt.value,
                                                  maxLimit
                                                )
                                              }
                                              onClick={e => e.stopPropagation()}
                                            />
                                            <span className="leading-snug">
                                              {opt.label}
                                            </span>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                )}

                                {errorMsg && (
                                  <p className="text-xs font-medium text-red-600">
                                    {errorMsg}
                                  </p>
                                )}
                              </div>
                            )
                          })}
                        </div>

                        <div className="pt-2">
                          <Button
                            type="submit"
                            variant="accent"
                            size="lg"
                            disabled={isSubmitting}
                            className="w-full sm:w-auto"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2
                                  size={18}
                                  className="animate-spin"
                                  aria-hidden="true"
                                />
                                Đang gửi hồ sơ...
                              </>
                            ) : (
                              <>
                                <Send size={16} aria-hidden="true" />
                                Gửi hồ sơ ứng tuyển
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* RIGHT COLUMN: Sticky Job Overview Sidebar (Greenhouse / Ashby style) */}
            <div className="lg:col-span-4 lg:sticky lg:top-24 space-y-4">
              <Card>
                <CardContent className="p-6 sm:p-7 space-y-6">
                  <h2 className="text-base font-bold text-[#1f4171]">
                    Thông tin vị trí
                  </h2>

                  <div className="space-y-4 text-sm">
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#e8fcf9] text-[#0f766e]">
                        <Banknote size={18} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Mức thu nhập</p>
                        <p className="font-bold text-[#0f766e]">
                          {job.salary_display}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f5f7fa] text-[#1f4171]">
                        <Briefcase size={18} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Khối làm việc</p>
                        <p className="font-semibold text-[#142d4f]">
                          {job.block_label}
                        </p>
                      </div>
                    </div>

                    {job.tags.length > 0 && (
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f5f7fa] text-[#1f4171]">
                          <Clock size={18} aria-hidden="true" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">
                            Hình thức làm việc
                          </p>
                          <p className="font-semibold text-[#142d4f]">
                            {job.tags.join(" / ")}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f5f7fa] text-[#1f4171]">
                        <MapPin size={18} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Khu vực</p>
                        <p className="font-semibold text-[#142d4f]">
                          {job.location}
                        </p>
                      </div>
                    </div>

                    {job.recruitment_period !== "-" && (
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f5f7fa] text-[#1f4171]">
                          <Calendar size={18} aria-hidden="true" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">
                            Hạn nộp hồ sơ
                          </p>
                          <p className="font-semibold text-[#142d4f]">
                            {job.recruitment_period}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2.5 pt-2">
                    <Button
                      type="button"
                      variant="accent"
                      size="lg"
                      onClick={scrollToApplyForm}
                      className="w-full"
                    >
                      <Send size={16} aria-hidden="true" />
                      {alreadyApplied ? "Ứng tuyển lại" : "Ứng tuyển ngay"}
                    </Button>

                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleCopyLink}
                      className="w-full"
                    >
                      {copiedLink ? (
                        <>
                          <Check size={15} aria-hidden="true" />
                          Đã sao chép liên kết
                        </>
                      ) : (
                        <>
                          <Share2 size={15} aria-hidden="true" />
                          Chia sẻ vị trí này
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

